import { Control, UseFormReturn, UseFormSetError, UseFormSetValue, useWatch } from 'react-hook-form';
import { TextInputField } from '@brighte/form-components';
import { CodeAcceptanceStatus, VerificationType } from '@brighte/validation';
import { AuthenticationFlowType } from '../graphql';
import { useUpdateContactDetails } from '../hooks';
import { LABELS } from '../utils';
import { FormContactFieldsType, VerificationDetailsType } from './ContactFields';
import SecureCode, { ResentSecureCodeData } from './SecureCode';

type UpdateFieldsProps = {
  isMobileOnly: boolean;
  verificationDetails?: VerificationDetailsType;
} & UseFormReturn<FormContactFieldsType>;

export function UpdateFields({ isMobileOnly, verificationDetails, ...formContext }: UpdateFieldsProps) {
  const { EMAIL, MOBILE_NUMBER, UPDATE_EMAIL, UPDATE_MOBILE } = LABELS;

  const { sendUpdateContactDetailsVerification, isLoading } = useUpdateContactDetails();
  const { control } = formContext;
  const [newEmail, newMobile, codeAccepted, updateOtpSent] = useWatch({
    control,
    name: ['newEmail', 'newMobile', 'codeAccepted', 'updateOtpSent'],
  });

  const otpData = {
    email: isMobileOnly ? newEmail : verificationDetails?.email,
    mobile: isMobileOnly ? verificationDetails?.mobile : newMobile,
    type: isMobileOnly ? AuthenticationFlowType.EMAIL : AuthenticationFlowType.SMS,
  };

  if (!codeAccepted || codeAccepted === CodeAcceptanceStatus.NotAccepted) {
    return null;
  }

  const onResetCode = (data: ResentSecureCodeData) => {
    sendUpdateContactDetailsVerification({
      email: data.email,
      verificationType: data.type === AuthenticationFlowType.EMAIL ? VerificationType.Email : VerificationType.Sms,
      mobile: newMobile,
    });
  };

  const renderUpdateFields = () => {
    if (isMobileOnly) {
      return (
        <TextInputField
          control={control}
          name="newEmail"
          label={EMAIL}
          data-testid="new-email"
          FieldProps={{
            description: UPDATE_EMAIL.replace('%USER_EMAIL%', verificationDetails?.maskedEmail ?? ''),
          }}
          type="email"
        />
      );
    }

    return (
      <TextInputField
        control={control}
        name="newMobile"
        label={MOBILE_NUMBER}
        data-testid="new-mobile"
        FieldProps={{
          description: UPDATE_MOBILE.replace('%USER_MOBILE%', verificationDetails?.maskedMobile ?? ''),
        }}
        type="tel"
      />
    );
  };

  return (
    <>
      {renderUpdateFields()}
      {updateOtpSent ? (
        <SecureCode
          isMobile={!isMobileOnly}
          isCodeAccepted={false}
          name="updateOtp"
          dataTestId="update-otp"
          data={otpData}
          loading={isLoading}
          onResetCode={onResetCode}
          {...formContext}
        />
      ) : null}
    </>
  );
}
